/**
 * 「カリキュラムの進捗」block inside the 現在の進行情報 box of the learner detail page.
 * Read-only admin/RSO port of the learner app's LearningProgressCard.
 */

import { useLearnerCurriculumProgress } from "@/api/services/main/learning-progress";
import { IconFlag } from "@/assets/icons";
import { CurriculumGroupAccordion } from "@/features/learning-progress/components/learner-detail/curriculum-progress/CurriculumGroupAccordion";
import { toCurriculumGroups } from "@/features/learning-progress/utils/curriculum-groups";
import { background, divider, primary, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { Box, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { FC, useMemo } from "react";

type TCurriculumProgressBlockProps = {
  learnerId: number | string;
};

export const CurriculumProgressBlock: FC<TCurriculumProgressBlockProps> = ({ learnerId }) => {
  const { data: res, isLoading } = useLearnerCurriculumProgress({ learnerId });
  const progress = res?.data;

  /* WEEKs grouped by 業種/コース — falls back to a single group while BE returns the flat list */
  const groups = useMemo(() => (progress ? toCurriculumGroups(progress) : []), [progress]);

  /* Hide the whole block when no subject has been started and no assessment was taken */
  const hasAnyActivity = progress?.subjects?.some((s) => s.status !== "not_started" || s.assessedLevel !== null);

  if (isLoading) {
    return (
      <Stack alignItems="center" py={3}>
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (!progress || !hasAnyActivity) return null;

  return (
    <Stack
      sx={{
        borderRadius: rounded.xs,
        border: `1px solid ${divider.high}`,
        bgcolor: background.white,
        p: spacing.sm,
        gap: "20px",
        marginTop: "24px",
      }}
    >
      {/* Header */}
      <Stack gap={spacing.xs}>
        <Box display="flex" alignItems="center" gap={spacing.xxs}>
          <IconFlag color={primary[500]} width={24} height={24} />
          <Typography variant="sub16Bold" color={text.primary}>
            カリキュラムの進捗
          </Typography>
        </Box>
        <Divider sx={{ borderBottom: `1px solid ${divider.high}` }} />
      </Stack>

      {/* Curriculum groups — bar + WEEK chips only, no per-WEEK detail on the admin side */}
      {groups.map((group) => (
        <CurriculumGroupAccordion key={group.id} group={group} />
      ))}
    </Stack>
  );
};
